import { computed, getCurrentInstance } from '@vue/composition-api'

export default function useTour () {
    const vm = getCurrentInstance().proxy
    const store = vm.$store
    const STORAGE_KEY = 'doneTour'
    const tourInstance = computed(() => store.getters['appConfig/tourInstance'])
    const isDoneTourDashboard = computed(() => !!JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')?.dashboard)
    const isDoneTourEditDashboard = computed(() => !!JSON.parse(localStorage.getItem(STORAGE_KEY) ?? '{}')?.editDashboard)
    const doneTour = (key) => {
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({
                ...JSON.parse(localStorage.getItem(STORAGE_KEY)),
                [key]: true
            })
        )
    }

    const DASHBOARD = 'dashboard'
    const EDIT_DASHBOARD = 'editDashboard'

    const isTourActive = computed(() => store.getters['appConfig/isTourActive'])
    const updateIsTourActive = (isTourActive) => store.commit('appConfig/UPDATE_IS_TOUR_ACTIVE', isTourActive)
    const updateTourInstance = (tourInstance) => store.commit('appConfig/UPDATE_TOUR_INSTANCE', tourInstance)
    const createTourStep = (steps) => {
        const tourInstance = vm.$shepherd({
            exitOnEsc: false,
            defaultStepOptions: {
                scrollTo: { behavior: 'smooth', block: 'center' }
            },
            useModalOverlay: true
        }).addSteps(steps)
        updateTourInstance(tourInstance)
        return tourInstance
    }
    const cancelTour = () => {
        if (!tourInstance.value) return
        tourInstance.value.cancel()
        updateIsTourActive(false)
    }

    const startTourDashboard = () => {
        createTourStep([
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.New function')}</span><span class="shepherd-header-page">1/3</span></div>`,
                text: `${vm.$t('org.tour.Dashboard editor has been added! Manage your data efficiently by organizing your personalized dashboard')}`,
                attachTo: {
                    element: '#tour-dashboard-step-1',
                    on: 'top'
                },
                modalOverlayOpeningRadius: 5,
                beforeShowPromise: function () {
                    return new Promise((resolve, reject) =>
                        setTimeout(() => {
                            resolve()
                        }, 500)
                    )
                },
                canClickTarget: false,
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.next()
                        },
                        text: `${vm.$t('org.tour.Next')}`
                    }
                ]
            },
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.Data Filtering')}</span><span class="shepherd-header-page">2/3</span></div>`,
                text: `${vm.$t('org.tour.You can focus on the information you want by turning the legend on and off')}`,
                attachTo: {
                    element: '#tour-dashboard-step-2',
                    on: 'left'
                },
                modalOverlayOpeningRadius: 5,
                when: {
                    show: function () {
                        window.scrollTo(0, 200)
                    }
                },
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.back()
                        },
                        secondary: true,
                        text: `${vm.$t('org.tour.Prev')}`
                    },
                    {
                        action: function () {
                            return this.next()
                        },
                        text: `${vm.$t('org.tour.Next')}`
                    }
                ]
            },
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.View Card Details')}</span><span class="shepherd-header-page">3/3</span></div>`,
                text: `${vm.$t('org.tour.If the magnification icon is on the card, you can view the data details')}`,
                attachTo: {
                    element: '#tour-dashboard-step-3',
                    on: 'left'
                },
                modalOverlayOpeningRadius: 5,
                when: {
                    show: function () {
                        window.scrollTo(0, 400)
                    }
                },
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.back()
                        },
                        secondary: true,
                        text: `${vm.$t('org.tour.Prev')}`
                    },
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(DASHBOARD)
                            return this.complete()
                        },
                        text: `${vm.$t('org.tour.Finish')}`
                    }
                ]
            }
        ]).start().then(() => {
            updateIsTourActive(true)
        })
    }

    const startTourDashboardEdit = () => {
        createTourStep([
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.Add and delete cards')}</span><span class="shepherd-header-page">1/5</span></div>`,
                text: `${vm.$t('org.tour.There is a editing area where you can see card types and edit them conveniently You can add or remove cards through the checkbox')}`,
                beforeShowPromise: function () {
                    return new Promise((resolve, reject) =>
                        setTimeout(() => {
                            resolve()
                        }, 500)
                    )
                },
                attachTo: {
                    element: '#sidebar-dashboard',
                    on: 'right'
                },
                modalOverlayOpeningRadius: 0,
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(EDIT_DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.next()
                        },
                        text: `${vm.$t('org.tour.Next')}`
                    }
                ]
            },
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.Edit Initialization')}</span><span class="shepherd-header-page">2/5</span></div>`,
                text: `${vm.$t('org.tour.If you want to return the edit to its default settings, click the Reset button')}`,
                attachTo: {
                    element: '#tour-edit-dashboard-step-2',
                    on: 'bottom'
                },
                modalOverlayOpeningRadius: 5,
                canClickTarget: false,
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(EDIT_DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.back()
                        },
                        secondary: true,
                        text: `${vm.$t('org.tour.Prev')}`
                    },
                    {
                        action: function () {
                            return this.next()
                        },
                        text: `${vm.$t('org.tour.Next')}`
                    }
                ]
            },
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.Card Removal')}</span><span class="shepherd-header-page">3/5</span></div>`,
                text: `${vm.$t('org.tour.Delete cards that you dont need by clicking the Remove icon If you want to add again, try the dashboard card bar on the left')}`,
                attachTo: {
                    element: '#tour-edit-dashboard-step-3',
                    on: 'bottom'
                },
                modalOverlayOpeningRadius: 11,
                canClickTarget: false,
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(EDIT_DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.back()
                        },
                        secondary: true,
                        text: `${vm.$t('org.tour.Prev')}`
                    },
                    {
                        action: function () {
                            return this.next()
                        },
                        text: `${vm.$t('org.tour.Next')}`
                    }
                ]
            },
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.Size control')}</span><span class="shepherd-header-page">4/5</span></div>`,
                text: `${vm.$t('org.tour.Click and drag the icon to adjust the card size freely')}`,
                attachTo: {
                    element: '#tour-edit-dashboard-step-4',
                    on: 'left'
                },
                modalOverlayOpeningRadius: 5,
                canClickTarget: false,
                when: {
                    show: function () {
                        window.scrollTo(0, 200)
                    }
                },
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(EDIT_DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.back()
                        },
                        secondary: true,
                        text: `${vm.$t('org.tour.Prev')}`
                    },
                    {
                        action: function () {
                            return this.next()
                        },
                        text: `${vm.$t('org.tour.Next')}`
                    }
                ]
            },
            {
                title: `<div class="shephered-header-group"><span>${vm.$t('org.tour.Position shifting')}</span><span class="shepherd-header-page">5/5</span></div>`,
                text: `${vm.$t('org.tour.You can drag the card to another location within the dashboard')}`,
                attachTo: {
                    element: '#tour-edit-dashboard-step-5',
                    on: 'top'
                },
                modalOverlayOpeningRadius: 5,
                canClickTarget: false,
                when: {
                    show: function () {
                        window.scrollTo(0, 400)
                    }
                },
                buttons: [
                    {
                        action: function () {
                            updateIsTourActive(false)
                            doneTour(EDIT_DASHBOARD)
                            return this.cancel()
                        },
                        secondary: true,
                        text: 'SKIP',
                        classes: 'btn-skip'
                    },
                    {
                        action: function () {
                            return this.back()
                        },
                        secondary: true,
                        text: `${vm.$t('org.tour.Prev')}`
                    },
                    {
                        action: function () {
                            doneTour(EDIT_DASHBOARD)
                            updateIsTourActive(false)
                            return this.complete()
                        },
                        text: `${vm.$t('org.tour.Finish')}`
                    }
                ]
            }
        ]).start().then(() => {
            updateIsTourActive(true)
        })
    }

    return {
        tourInstance,
        isDoneTourDashboard,
        isDoneTourEditDashboard,
        isTourActive,
        cancelTour,
        updateTourInstance,
        updateIsTourActive,
        startTourDashboard,
        startTourDashboardEdit
    }
}
